import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Copy, Share2, QrCode, Trophy, Users, 
  Gift, TrendingUp, ExternalLink, Download,
  CheckCircle, Clock, DollarSign
} from 'lucide-react';
import QRCode from 'qrcode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralProgram = ({ onRefresh }) => {
  const [myCode, setMyCode] = useState(null);
  const [myReferrals, setMyReferrals] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [clinicInfo, setClinicInfo] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [codeRes, referralsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API_URL}/api/marketing/referrals/my-code`, { headers }),
        axios.get(`${API_URL}/api/marketing/referrals/my-referrals`, { headers }),
        axios.get(`${API_URL}/api/marketing/referrals/leaderboard`, { headers })
      ]);

      setMyCode(codeRes.data);
      setMyReferrals(referralsRes.data.referrals);
      setLeaderboard(leaderboardRes.data.leaderboard);

      // Set clinic info from backend response
      if (codeRes.data?.clinic_slug) {
        setClinicInfo({
          subdomain: codeRes.data.clinic_slug,
          slug: codeRes.data.clinic_slug,
          name: codeRes.data.clinic_name
        });
      } else {
        // Fallback: Get clinic info from localStorage
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          setClinicInfo(user.clinic || {});
        }
      }

      // Generate QR code using backend-provided URL
      if (codeRes.data?.code) {
        generateQRCode(codeRes.data.code, codeRes.data.referral_url);
      }
    } catch (error) {
      console.error('Error loading referral data:', error);
      toast.error('Failed to load referral program data');
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async (code, url = null) => {
    try {
      // Use backend-provided URL or construct one
      const referralUrl = url || getReferralUrl();
      if (!referralUrl) {
        console.warn('No referral URL available for QR code');
        return;
      }
      const qrDataUrl = await QRCode.toDataURL(referralUrl, {
        width: 200,
        margin: 2,
        color: { dark: '#7c3aed', light: '#ffffff' }
      });
      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      console.error('QR code generation error:', error);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const getReferralUrl = () => {
    // Use backend-provided URL first
    if (myCode?.referral_url) {
      return myCode.referral_url;
    }
    // Fallback to constructing URL
    const clinicSlug = myCode?.clinic_slug || clinicInfo?.subdomain || clinicInfo?.slug || clinicInfo?.id || '';
    if (!clinicSlug) {
      return `https://mymedmember.com/register-patient?ref=${myCode?.code}`;
    }
    return `https://mymedmember.com/join/${clinicSlug}?ref=${myCode?.code}`;
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.download = `referral-qr-${myCode?.code}.png`;
    link.href = qrCodeUrl;
    link.click();
  };

  const shareReferral = async () => {
    const url = getReferralUrl();
    const text = `Join ${clinicInfo?.name || 'our clinic'} membership and get ${myCode?.discount_percent}% off! Use my referral code: ${myCode?.code}`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join My Clinic', text, url });
      } catch (error) {
        copyToClipboard(url, 'Referral link');
      }
    } else {
      copyToClipboard(url, 'Referral link');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="referral-program">
      {/* My Referral Code Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Referral Card */}
        <Card className="lg:col-span-2 bg-gradient-to-br from-purple-600 to-pink-600 text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Share2 className="h-5 w-5" />
              Your Referral Code
            </CardTitle>
            <CardDescription className="text-purple-100">
              Share this code with friends and family
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Code Display */}
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-100 mb-1">Your unique code</p>
                  <p className="text-3xl font-bold font-mono tracking-wider">
                    {myCode?.code || 'Loading...'}
                  </p>
                </div>
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={() => copyToClipboard(myCode?.code, 'Code')}
                  data-testid="copy-code-btn"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </Button>
              </div>
            </div>

            {/* Referral URL */}
            <div className="space-y-2">
              <p className="text-sm text-purple-100">Referral Link</p>
              <div className="flex gap-2">
                <Input 
                  value={getReferralUrl()}
                  readOnly
                  className="bg-white/20 border-white/30 text-white placeholder:text-purple-200"
                />
                <Button 
                  variant="secondary" 
                  onClick={() => copyToClipboard(getReferralUrl(), 'Link')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex gap-2 pt-2">
              <Button 
                className="flex-1 bg-white text-purple-600 hover:bg-purple-50"
                onClick={shareReferral}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Share
              </Button>
              <Button 
                variant="outline" 
                className="border-white/50 text-white hover:bg-white/10"
                onClick={downloadQRCode}
              >
                <Download className="h-4 w-4 mr-2" />
                QR Code
              </Button>
            </div>

            {/* Reward Info */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
              <div>
                <p className="text-sm text-purple-200">Friend gets</p>
                <p className="text-xl font-bold">{myCode?.discount_percent || 10}% OFF</p>
              </div>
              <div>
                <p className="text-sm text-purple-200">You earn</p>
                <p className="text-xl font-bold">{myCode?.referrer_reward_percent || 10}% reward</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* QR Code Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <QrCode className="h-5 w-5 text-purple-600" />
              QR Code
            </CardTitle>
            <CardDescription>
              Print or display for easy scanning
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            {qrCodeUrl ? (
              <>
                <img 
                  src={qrCodeUrl} 
                  alt="Referral QR Code" 
                  className="w-40 h-40 rounded-lg border"
                />
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Scan to join with your referral code
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={downloadQRCode}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </Button>
              </>
            ) : (
              <div className="w-40 h-40 bg-gray-100 rounded-lg flex items-center justify-center">
                <p className="text-gray-400 text-sm">Loading...</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Successful Referrals</p>
                <p className="text-2xl font-bold">{myCode?.stats?.total_referrals || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold">{myCode?.stats?.pending_referrals || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Earnings</p>
                <p className="text-2xl font-bold">${(myCode?.stats?.total_earnings || 0).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Referrals List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              My Referrals
            </CardTitle>
            <CardDescription>People who joined using your code</CardDescription>
          </CardHeader>
          <CardContent>
            {myReferrals.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Gift className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No referrals yet</p>
                <p className="text-sm">Share your code to start earning rewards!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myReferrals.slice(0, 5).map((referral, index) => (
                  <div 
                    key={referral.id || index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-medium">
                        {(referral.referred_member_name || 'U')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{referral.referred_member_name || 'New Member'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(referral.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Badge variant={referral.status === 'completed' ? 'default' : 'secondary'}>
                      {referral.status === 'completed' ? 'Completed' : 'Pending'}
                    </Badge>
                  </div>
                ))}
                {myReferrals.length > 5 && (
                  <p className="text-sm text-center text-gray-500">
                    +{myReferrals.length - 5} more referrals
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Top Referrers
            </CardTitle>
            <CardDescription>Leaderboard this month</CardDescription>
          </CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Trophy className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No referrals yet this month</p>
                <p className="text-sm">Be the first to refer a friend!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((entry, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      index === 0 ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200' :
                      index === 1 ? 'bg-gray-100' :
                      index === 2 ? 'bg-orange-50' :
                      'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-amber-400 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-orange-400 text-white' :
                        'bg-gray-300 text-gray-700'
                      }`}>
                        {index + 1}
                      </div>
                      <span className="font-medium">{entry.owner_name}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-purple-600">{entry.total_referrals}</p>
                      <p className="text-xs text-gray-500">referrals</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* How It Works */}
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
        <CardHeader>
          <CardTitle className="text-lg text-purple-900">How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="h-12 w-12 rounded-full bg-purple-600 text-white flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                1
              </div>
              <h4 className="font-medium text-purple-900">Share Your Code</h4>
              <p className="text-sm text-purple-700 mt-1">
                Send your unique code or link to friends and family
              </p>
            </div>
            <div className="text-center">
              <div className="h-12 w-12 rounded-full bg-purple-600 text-white flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                2
              </div>
              <h4 className="font-medium text-purple-900">They Sign Up</h4>
              <p className="text-sm text-purple-700 mt-1">
                They get {myCode?.discount_percent || 10}% off their first membership
              </p>
            </div>
            <div className="text-center">
              <div className="h-12 w-12 rounded-full bg-purple-600 text-white flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                3
              </div>
              <h4 className="font-medium text-purple-900">You Earn Rewards</h4>
              <p className="text-sm text-purple-700 mt-1">
                Earn {myCode?.referrer_reward_percent || 10}% reward for each referral
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralProgram;
